import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../hooks/use-is-mounted";
import axios, {CancelToken} from "axios";
import {Autocomplete, AutocompleteInputChangeReason, AutocompleteProps, Box, createFilterOptions, FilterOptionsState, Skeleton, TextField, TextFieldProps} from "@mui/material";
import {Autocomplete as FormAutocomplete} from "mui-rff";

/**
 * Props interface for the useAutocomplete hook
 * Defines the configuration for fetching and managing autocomplete options
 *
 * @property getOptions - Async function to fetch options, accepts cancel token and optional search keywords
 * @property keywordsBased - Whether to fetch options based on user input (true) or all at once (false)
 * @property timeout - Debounce timeout in milliseconds for option fetching
 */
interface UseAutocompleteProps {
    getOptions: (cancelToken: CancelToken, keywords?: string) => Promise<any[]>;
    options?: readonly any[];
    keywordsBased?: boolean;
    canSearchEmptyKeywords?: boolean;
    shouldRemoveOptionsOnEmptyKeywords?: boolean;
    timeout?: number;
}

/**
 * Return type for the useAutocomplete hook
 * Contains state and setters for managing the autocomplete functionality
 *
 * @property options - Current list of autocomplete options
 * @property setOptions - Function to update options list
 * @property canFetchOptions - Whether options can be fetched (usually true after focus)
 * @property setCanFetOptions - Function to update fetch permission
 * @property keywords - Current search input
 * @property setKeywords - Function to update search input
 * @property loadingOptions - Whether options are currently being fetched
 * @property setLoadingOptions - Function to update loading state
 */
interface UseAutocompleteReturn {
    options: any[];
    setOptions: React.Dispatch<React.SetStateAction<any[]>>;
    canFetchOptions: boolean;
    setCanFetOptions: React.Dispatch<React.SetStateAction<boolean>>;
    keywords: string;
    setKeywords: React.Dispatch<React.SetStateAction<string>>;
    loadingOptions: boolean;
    setLoadingOptions: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * Custom hook for managing autocomplete functionality
 * Handles option fetching, debouncing, and state management
 *
 * Features:
 * - Debounced option fetching
 * - Keyword-based or full list fetching
 * - Loading state management
 * - Cleanup on unmount
 * - Cancellation of pending requests
 *
 * @param props - Configuration options for the hook
 * @param dependencies - Array of dependencies that trigger option refetching
 * @returns State and functions for managing autocomplete behavior
 */
const useAutocomplete = (
    {
        getOptions,
        options: optionsProp,
        keywordsBased = true,
        timeout = 1000,
        canSearchEmptyKeywords = false,
        shouldRemoveOptionsOnEmptyKeywords = true,
    }: UseAutocompleteProps,
    dependencies: readonly any[] = [],
): UseAutocompleteReturn => {
    const [options, setOptions] = useState<any[]>(optionsProp?.map(e => e) ?? []);
    const [loadingOptions, setLoadingOptions] = useState(false);
    const [canFetchOptions, setCanFetOptions] = useState(false);
    const [keywords, setKeywords] = useState('');
    const isMounted = useIsMounted();

    /**
     * Fetches options based on search keywords
     * Handles component unmounting and request cancellation
     */
    const fetchOptionsViaKeywords = useCallback(async (keywords: string, cancelToken: CancelToken) => {
        if (!isMounted() || !!cancelToken.reason)
            return;
        if (!keywords && !canSearchEmptyKeywords) {
            if (shouldRemoveOptionsOnEmptyKeywords)
                setOptions([]);
            return;
        }
        setLoadingOptions(true);
        const res = await getOptions(cancelToken, keywords);
        if (!isMounted())
            return;
        setOptions(res ?? []);
        setLoadingOptions(false);
    }, [getOptions, isMounted, canSearchEmptyKeywords, shouldRemoveOptionsOnEmptyKeywords]);

    // Stabilize dependencies array to prevent unnecessary re-renders
    const stringDependencies = useMemo(() => JSON.stringify(dependencies), [dependencies]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const stableDependencies = useMemo(() => dependencies, [stringDependencies]);

    // Effect for keyword-based option fetching
    useEffect(() => {
        if (!keywordsBased || !canFetchOptions || !getOptions)
            return;
        const cancelTokenSource = axios.CancelToken.source();
        setTimeout(() => void fetchOptionsViaKeywords(keywords, cancelTokenSource.token), timeout);
        return () => cancelTokenSource.cancel();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [keywordsBased, canFetchOptions, timeout, keywords, fetchOptionsViaKeywords, getOptions, ...stableDependencies]);

    /**
     * Fetches all options without keywords
     * Used when keywordsBased is false
     */
    const fetchOptions = useCallback(async (cancelToken: CancelToken) => {
        if (!isMounted())
            return;
        setLoadingOptions(true);
        const res = await getOptions(cancelToken);
        if (!isMounted())
            return;
        setOptions(res ?? []);
        setLoadingOptions(false);
    }, [getOptions, isMounted]);

    /**
     * Fetches the options from the api if the canFetchOptions and the fetching is non-keywords based.
     */
    useEffect(() => {
        if (keywordsBased || !canFetchOptions || !getOptions)
            return;
        const cancelTokenSource = axios.CancelToken.source();
        void fetchOptions(cancelTokenSource.token)
        return () => cancelTokenSource.cancel();
        // eslint-disable-next-line
    }, [keywordsBased, canFetchOptions, fetchOptions, getOptions, ...stableDependencies]);


    /**
     * Updates options state when optionsProp changes
     * Allows external control of options list
     */
    useEffect(() => {
        if (!getOptions) {
            setOptions(optionsProp?.map(e => e) ?? []);
        }
    }, [getOptions, optionsProp]);

    return {
        options,
        setOptions,
        canFetchOptions,
        setCanFetOptions,
        keywords,
        setKeywords,
        loadingOptions,
        setLoadingOptions,
    };
};

/**
 * Props interface for the AutocompleteBase component
 * Extends Material-UI's Autocomplete props with additional functionality
 *
 * @template T - Type of the option items
 * @property textFieldProps - Props to pass to the underlying TextField component
 * @property label - Label for the autocomplete field
 * @property form - Whether the component is used within a form
 * @property name - Name attribute for form integration
 * @property required - Whether the field is required in forms
 * @property getOptions - Function to fetch options from an API
 * @property keywordsBased - Whether to fetch options based on user input
 * @property timeout - Debounce timeout for option fetching
 * @property dependencies - Array of values that trigger option refetching
 */
export interface AutocompleteBaseProps<T, M extends boolean = false, DC extends boolean = false, FS extends boolean = false> extends Omit<AutocompleteProps<T, M, DC, FS>, 'renderInput'> {
    textFieldProps?: Partial<TextFieldProps>;
    label?: string;
    form?: boolean;
    name?: string;
    required?: boolean;
    getOptions: (cancelToken: CancelToken, keywords?: string) => Promise<T[]>;
    keywordsBased?: boolean;
    timeout?: number;
    dependencies?: readonly any[];
    canSearchEmptyKeywords?: boolean;
    shouldRemoveOptionsOnEmptyKeywords?: boolean;
    disableKeywordReset?: boolean;
}

/**
 * Base Autocomplete Component
 *
 * A reusable autocomplete component that provides:
 * - Async option loading with debouncing
 * - Loading state with skeleton placeholders
 * - Form integration support
 * - Customizable styling and behavior
 * - Keyboard navigation
 * - Clear and dropdown indicators
 *
 * Can be used in two modes:
 * 1. Standalone component (form=false)
 * 2. Form-integrated component (form=true)
 *
 * @template T - Type of the option items
 */
function AutocompleteBase<T, M extends boolean = false, DC extends boolean = false, FS extends boolean = false>({
                                                                                                                    label,
                                                                                                                    textFieldProps = {},
                                                                                                                    form = false,
                                                                                                                    name = undefined,
                                                                                                                    required = false,
                                                                                                                    getOptions,
                                                                                                                    canSearchEmptyKeywords,
                                                                                                                    shouldRemoveOptionsOnEmptyKeywords,
                                                                                                                    keywordsBased = undefined,
                                                                                                                    timeout = undefined,
                                                                                                                    dependencies = [],
                                                                                                                    ...props
                                                                                                                }: AutocompleteBaseProps<T, M, DC, FS>) {
    const {onFocus: onFocusProps, onInputChange: onInputChangeProps, filterOptions: propsFilterOptions, disableKeywordReset} = props;

    // Initialize autocomplete functionality with the useAutocomplete hook
    const {
        options,
        setCanFetOptions,
        setKeywords,
        loadingOptions,
    } = useAutocomplete({
        getOptions,
        options: props.options,
        keywordsBased,
        timeout,
        canSearchEmptyKeywords,
        shouldRemoveOptionsOnEmptyKeywords,
    }, dependencies);

    /**
     * Handles focus event
     * Enables option fetching and calls custom focus handler if provided
     */
    const onFocus = useCallback((event: React.FocusEvent<HTMLInputElement>) => {
        setCanFetOptions(true);
        if (onFocusProps) {
            onFocusProps(event);
        }
    }, [onFocusProps, setCanFetOptions]);

    /**
     * Handles input changes
     * Updates search keywords and calls custom input change handler if provided
     */
    const onInputChange = useCallback((
        event: React.SyntheticEvent,
        value: string,
        reason: AutocompleteInputChangeReason
    ) => {
        if ((reason !== 'reset' || disableKeywordReset)) {
            setKeywords(value);
        }
        if (onInputChangeProps) {
            onInputChangeProps(event, value, reason);
        }
    }, [onInputChangeProps, setKeywords, disableKeywordReset]);

    const defaultFilterOptions = useMemo(() => createFilterOptions<T>(), []);

    /**
     * Filters options based on loading state and custom filter function
     * Returns empty array while loading to prevent showing stale options
     */
    const filterOptions = useCallback((options: T[], state: FilterOptionsState<T>) => {
        if (loadingOptions) {
            return [];
        }
        if (propsFilterOptions) {
            return propsFilterOptions(options, state);
        }
        if (!keywordsBased) {
            return defaultFilterOptions(options, state);
        }
        return options;
    }, [defaultFilterOptions, keywordsBased, loadingOptions, propsFilterOptions]);

    return (
        <>
            {/* Standalone Autocomplete */}
            {
                !form &&
                <Autocomplete<T, M, DC, FS>
                    {...props}
                    {...(props.multiple && {
                        disableCloseOnSelect: true,
                    })}
                    onFocus={onFocus}
                    options={loadingOptions ? [] : options}
                    value={props.value}
                    onChange={props.onChange}
                    onInputChange={onInputChange}
                    filterOptions={filterOptions}
                    loading={loadingOptions}
                    loadingText={
                        <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, padding: 0.5}}>
                            {/* Loading skeleton placeholders */}
                            {
                                Array.from({length: 5}).map((_, i) => (
                                    <Skeleton
                                        key={i}
                                        sx={{transform: 'none'}}
                                        width={'100%'}
                                        height={40}
                                    />
                                ))
                            }
                        </Box>
                    }
                    componentsProps={{
                        clearIndicator: {color: 'default', ...(props.componentsProps?.clearIndicator ?? {})},
                        popupIndicator: {color: 'default', ...(props.componentsProps?.popupIndicator ?? {})},
                        ...(props.componentsProps ?? {}),
                    }}
                    renderInput={(params) =>
                        <TextField
                            {...params}
                            {...textFieldProps}
                            label={label}
                            InputProps={{
                                ...(params.InputProps ?? {}),
                                ...(textFieldProps?.InputProps ?? {}),
                                startAdornment: (
                                    <>
                                        {textFieldProps?.InputProps?.startAdornment}
                                        {params.InputProps?.startAdornment}
                                    </>
                                ),
                                endAdornment: (
                                    <>
                                        {textFieldProps?.InputProps?.endAdornment}
                                        {params.InputProps?.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    }
                />
            }
            {/* Form-integrated Autocomplete */}
            {
                form &&
                <FormAutocomplete<T, M, DC, FS>
                    {...props}
                    {...(props.multiple && {
                        disableCloseOnSelect: true,
                    })}
                    name={name ?? ''}
                    required={required}
                    onFocus={onFocus}
                    options={loadingOptions ? [] : options}
                    onInputChange={onInputChange}
                    filterOptions={filterOptions}
                    label={label}
                    loading={loadingOptions}
                    loadingText={
                        <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, padding: 0.5}}>
                            {/* Loading skeleton placeholders */}
                            {
                                Array.from({length: 5}).map((_, i) => (
                                    <Skeleton
                                        key={i}
                                        sx={{transform: 'none'}}
                                        width={'100%'}
                                        height={40}
                                    />
                                ))
                            }
                        </Box>
                    }
                    componentsProps={{
                        clearIndicator: {color: 'default', ...(props.componentsProps?.clearIndicator ?? {})},
                        popupIndicator: {color: 'default', ...(props.componentsProps?.popupIndicator ?? {})},
                        ...(props.componentsProps ?? {}),
                    }}
                    textFieldProps={textFieldProps}
                />
            }
        </>
    );
}

export default AutocompleteBase; 